'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  PromoAfterItemUpload,
  usePromoAfterItemUploadContext,
} from '@marketplace-web/item-upload/after-upload-actions-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '@marketplace-web/browser/browser-storage-util'
import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import {
  prepareBumpOrder,
  transformPrepareBumpOrderResponse,
  BumpableItemPreCheckoutModel,
  prepareBumpOrderSvcVas,
  transformPrepareBumpOrderResponseSvcVas,
} from '@marketplace-web/vas/vas-data'

import {
  viewSingleCheckoutEvent,
  ExtraServiceOrderType,
  CheckoutOrderTypeMap,
} from '@marketplace-web/checkout/checkout-data'
import {
  GenericErrorModal,
  useNavigateToCheckout,
} from '@marketplace-web/checkout/checkout-feature'

import { PreCheckoutModal } from '../../../constants/bumps'
import { BUMP_MULTIPLE_ITEM_SELECTION_URL } from '../../../constants/routes'
import useBumpCheckoutContext from '../../../hooks/useBumpCheckoutContext'
import BumpSelectionConfirmationModal from './BumpSelectionConfirmationModal'
import BumpValuePropositionModal from './BumpValuePropositionModal'
import useBumpOptions from './hooks/useBumpOptions'
import BumpValuePropositionModalV3 from './BumpValuePropositionV3'

const VALUE_PROPOSITION_SHOWN_KEY = 'push_up_value_proposition_shown'

type Props = {
  isInitiatedFromMultiSelect?: boolean
}

const BumpCheckout = ({ isInitiatedFromMultiSelect = false }: Props) => {
  const { track } = useTracking()
  const { navigateToCheckout } = useNavigateToCheckout()
  const { selectedItems, isPreCheckoutOpen, setIsPreCheckoutOpen, setSelectedItems } =
    useBumpCheckoutContext()
  const trackExpose = useTrackAbTestCallback()
  const bumpValuePropositionUpdateAbTest = useAbTest('bump_value_proposition_update')
  const vasApiGatewaySwapBumpsOrdersAbTest = useAbTest('vas_api_gateway_swap_bumps_orders')
  const isVasApiGatewaySwapBumpsOrdersAbTestOn =
    vasApiGatewaySwapBumpsOrdersAbTest?.variant === 'on'

  const { uploadedItemId, currentPromotion, setCurrentPromotion } = usePromoAfterItemUploadContext()

  const [uiState, setUiState] = useState(UiState.Idle)
  const [activeModal, setActiveModal] = useState<PreCheckoutModal>(PreCheckoutModal.None)

  const selectedItemIds = selectedItems.map(item => item.id)

  const {
    fetch: prepareOrderCore,
    transformedData: orderCore,
    error: orderErrorCore,
  } = useFetch(prepareBumpOrder, transformPrepareBumpOrderResponse)

  const {
    fetch: prepareOrderSvcVas,
    transformedData: orderSvcVas,
    error: orderErrorSvcVas,
  } = useFetch(prepareBumpOrderSvcVas, transformPrepareBumpOrderResponseSvcVas)

  const prepareOrder = isVasApiGatewaySwapBumpsOrdersAbTestOn
    ? prepareOrderSvcVas
    : prepareOrderCore
  const order = isVasApiGatewaySwapBumpsOrdersAbTestOn ? orderSvcVas : orderCore
  const orderError = isVasApiGatewaySwapBumpsOrdersAbTestOn ? orderErrorSvcVas : orderErrorCore

  const {
    bumpOptions,
    isBumpOptionsLoading,
    bumpOptionsError,
    selectedBumpOption,
    updateSelectedBumpOption,
  } = useBumpOptions(activeModal)

  useEffect(
    function openPreCheckout() {
      if (!isPreCheckoutOpen) return

      const isValuePropositionSeen = getLocalStorageItem(VALUE_PROPOSITION_SHOWN_KEY) === 'true'

      if (!isValuePropositionSeen) {
        setLocalStorageItem(VALUE_PROPOSITION_SHOWN_KEY, 'true')
        setActiveModal(PreCheckoutModal.ValueProposition)

        return
      }

      setActiveModal(PreCheckoutModal.OptionSelection)
    },
    [isPreCheckoutOpen],
  )

  useEffect(
    function openPrecheckoutAfterUploadForm() {
      if (currentPromotion !== PromoAfterItemUpload.BumpCheckout) return
      if (!uploadedItemId) return

      const selectedItem: BumpableItemPreCheckoutModel = {
        id: uploadedItemId,
        title: null,
        thumbnail: null,
      }

      setSelectedItems([selectedItem])
      setActiveModal(PreCheckoutModal.OptionSelection)
      setCurrentPromotion(PromoAfterItemUpload.Empty)
    },
    [currentPromotion, uploadedItemId, setActiveModal, setCurrentPromotion, setSelectedItems],
  )

  useEffect(
    function setErrorModal() {
      if (!bumpOptionsError && !orderError) return

      setUiState(UiState.Failure)
      setActiveModal(PreCheckoutModal.GenericErrorModal)
    },
    [bumpOptionsError, orderError, setActiveModal],
  )

  const orderId = order?.id
  const orderType = CheckoutOrderTypeMap[ExtraServiceOrderType.PushUp]

  const fetchCheckout = useCallback(async () => {
    if (!orderId) return

    track(
      viewSingleCheckoutEvent({
        checkoutId: null,
        orderType,
        orderId: orderId.toString(),
      }),
    )

    const isError = await navigateToCheckout(orderId, orderType)

    if (isError) {
      setUiState(UiState.Failure)
      setActiveModal(PreCheckoutModal.GenericErrorModal)
    }
  }, [navigateToCheckout, orderId, orderType, track])

  useEffect(() => {
    if (!orderId) return

    fetchCheckout()
  }, [fetchCheckout, orderId, track])

  function openSelectionConfirmationModal() {
    setActiveModal(PreCheckoutModal.OptionSelection)
  }

  function openValuePropositionModal() {
    setActiveModal(PreCheckoutModal.ValueProposition)
  }

  function goToBumpPage() {
    navigateToPage(urlWithParams(BUMP_MULTIPLE_ITEM_SELECTION_URL, { item_ids: selectedItemIds }))
  }

  function closeCheckoutModal() {
    setActiveModal(PreCheckoutModal.None)
    setIsPreCheckoutOpen(false)
  }

  function handleConfirmSelection() {
    if (!selectedBumpOption) return

    setUiState(UiState.Pending)

    prepareOrder(selectedItemIds, selectedBumpOption.days, selectedBumpOption.international)

    trackExpose(vasApiGatewaySwapBumpsOrdersAbTest)
  }

  return (
    <>
      <BumpSelectionConfirmationModal
        show={activeModal === PreCheckoutModal.OptionSelection}
        isOrderLoading={uiState === UiState.Pending}
        bumpOptions={bumpOptions}
        isBumpOptionsLoading={isBumpOptionsLoading}
        selectedOption={selectedBumpOption}
        onAddButtonClick={isInitiatedFromMultiSelect ? closeCheckoutModal : goToBumpPage}
        onSelectedOptionChange={updateSelectedBumpOption}
        onConfirm={handleConfirmSelection}
        onCancel={closeCheckoutModal}
        onHelpButtonClick={openValuePropositionModal}
      />
      {bumpValuePropositionUpdateAbTest?.variant === 'on' ? (
        <BumpValuePropositionModalV3
          show={activeModal === PreCheckoutModal.ValueProposition}
          onCloseOrConfirm={openSelectionConfirmationModal}
        />
      ) : (
        <BumpValuePropositionModal
          show={activeModal === PreCheckoutModal.ValueProposition}
          hasLocalOptions={bumpOptions?.hasLocalOptions}
          onCloseOrConfirm={openSelectionConfirmationModal}
        />
      )}
      <GenericErrorModal
        isShown={activeModal === PreCheckoutModal.GenericErrorModal}
        onClose={closeCheckoutModal}
      />
    </>
  )
}

export default BumpCheckout
